<template>
  <div class="card">
    <div class="bg-blue-light p-1">
      <TitleButton
          :showBtn="false"
          title="My Dental Schedule"
          :showAddNew="false"
          :showExtraBtn="true"
          extraBtnTitle="Calendar View"
          @onClickExtraBtn="navigateToCalendarPage"
      />
    </div>
    <div class="my-2 px-2">
      <DateQuerySetter
          btnTitle="Go"
          @onClickGo="onClickToGo"
          class="tds-vds-query-setter"
      >
        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">Schedule Type</label>
          <v-select
              placeholder="Select Schedule Status"
              :options="activityLogTypeList"
              v-model="activityLogType"
              label="name"
              :reduce="name => name.value"
          />
        </div>

        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">Schedule Status</label>
          <v-select
              placeholder="Select Schedule Status"
              :options="taskCalendarStatusList"
              v-model="taskCalendarStatus"
              label="name"
              :reduce="name => name.value"
          />
        </div>

      </DateQuerySetter>
    </div>

    <div class="col-12">
      <div class="d-flex justify-content-end px-2">
        <button
            @click="goToPrint"
            :disabled="loading"
            class="btn btn-outline-secondary btn-sm btn-download-invoice waves-effect"
        >
          Print
        </button>
      </div>
    </div>

    <div class="col-12 px-2 mt-2 mb-4">
      <div class="card p-2" style="min-height: 200px">
        <div class="table-responsive">
          <table class="table table-bordered">
            <thead>
            <tr>
              <th>Patient Name(Patient Id)</th>
              <th>Gender</th>
              <th>Mobile No</th>
              <th>Age</th>
              <th>Schedule Date</th>
              <th>Doctor Name</th>
              <th>Service Name</th>
              <th>Status</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(item, i) in tableItems"
                :key="i"
                :style="{
                    backgroundColor: taskStatusColors[item.status].bg || '#f9fafb',
                    color: taskStatusColors[item.status].text || '#111827',
                  }"
            >
              <td>{{ item.patient_name }}({{ item.patient_serial_no }})</td>
              <td>{{ convertToTitleCase(item.patient_gender) }}</td>
              <td>{{ item.patient_mobile_no }}</td>
              <td>{{ item.patient_age }}</td>
              <td>{{ item.start_time }}</td>
              <td>{{ item.doctor_name }}</td>
              <td>{{ item.product_name }}</td>
              <td>{{ convertToTitleCase(item.status) }}</td>
            </tr>
            </tbody>
          </table>
          <p v-if="tableItems.length < 0" class="text-center mt-5">No data</p>
        </div>
      </div>
    </div>

    <div class="px-2 position-absolute bottom-0 mt-4">
      <Pagination :offset="params.offset" @onPageChange="onPageChange" ref="pagination"/>
    </div>

    <AddAppointmentModal
        v-if="store.state.isModalOpen"
        @onSubmit="getDentalScheduleList"
    />

    <Loader v-if="loading"/>

  </div>
</template>

<script setup>
import TitleButton from '@/components/atom/TitleButton';
import {inject, onMounted, reactive, ref} from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import Loader from '@/components/atom/LoaderComponent'
import Pagination from '@/components/atom/Pagination'
import DateQuerySetter from '@/components/atom/DateQuerySetter.vue';
import { computed } from 'vue';
import handleDentalOs from '@/services/modules/dental-os/dentalOs';
import AddAppointmentModal from '@/components/molecule/company/dental-os/appointment/AddAppointmentModal.vue';
import handleCompany from "@/services/modules/company";
import dentalSchedulePdf from '@/services/utils/pdf/dentalSchedulePdf';
import handleChartAccount from '@/services/modules/chartAccount'
import { taskStatusColors, convertToTitleCase } from '@/services/utils/global.js';

const store = useStore();
const route = useRoute();
const router = useRouter();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const contactProfile = ref('');
const loading = ref(false)
const tableItems = ref([]);
const dentalScheduleData = ref([]);
const params = ref({
  offset: 20,
});
const startDate = ref('');
const endDate = ref('');
const additionalQuery = {
  roles: "doctor,medical_officer",
}
const taskCalendarStatusList = ref([]);
const taskCalendarStatus = ref('');
const activityLogTypeList = ref([]);
const activityLogType = ref('');
const token = computed(() => localStorage.getItem('token'));
let companyInfo = reactive({})

const { fetchDentalScheduleList } = handleDentalOs();
const { fetchCompanyInfo } = handleCompany();
const { exportToPDF } = dentalSchedulePdf();
const { fetchHome } = handleChartAccount()

const onClickToGo = () => {
  getDentalScheduleList();
}

const setPaginationData = (data) => {
  store.commit('setPaginationData', data)
};

const setPagination = (data) => {
  setPaginationData({
    records: data.total,
    perPage: data.per_page,
    currentPage: data.current_page,
  })
}

function navigateToCalendarPage() {
  router.push({name: `my-dental-schedule`, params: route.params, query: route.query});
}

const onPageChange = async (page) => {
  let routeQuery = Object.assign({}, route.query)
  routeQuery.page = page
  await router.push({path: route.path, query: routeQuery})
  await getDentalScheduleList();
}

const getDentalScheduleList = async () => {
  const query = getQuery();
  loading.value = true
  tableItems.value = []

  await fetchDentalScheduleList(query).then(res => {
    if (!res.status){
      store.commit("resetPagination")
      return
    }
    tableItems.value = res.data.data;
    setPagination(res.data)
  }).catch(err => {
    console.log(err)
  }).finally(() => {
    loading.value = false
  })
}

const goToPrint = async () => {
  let query = getQuery();
  query += '&is_all=1';

  loading.value = true;
  dentalScheduleData.value = [];

  try {
    const res = await fetchDentalScheduleList(query);

    if (!res.status) {
      showError(res.message);
      return;
    }

    dentalScheduleData.value = res.data;
    exportToPDF(companyInfo, dentalScheduleData.value);
  } catch (err) {
    console.log("Something went wrong");
  } finally {
    loading.value = false;
  }
}

const setCompanyInfo = async () => {
  await fetchCompanyInfo(route.params.companyId).then((res) => {
    companyInfo = res.data;
  }).catch((error) => {
    console.log(error);
  });
}

const getQuery = () => {
  let query = `?company_id=${route.params.companyId}&is_my_contact=1`;
  if (route.query.page) query += '&page=' + route.query.page;
  if (route.query.start) query += '&start_date=' + route.query.start;
  if (route.query.end) query += '&end_date=' + route.query.end;
  if (taskCalendarStatus.value) query += `&status=${taskCalendarStatus.value}`;
  if (activityLogType.value) query += `&type=${activityLogType.value}`;
  query += '&offset=' + params.value.offset
  return query;
}

onMounted(() => {
  Promise.all([
    getDentalScheduleList(),
    setCompanyInfo(),
    fetchHome().then(res => {
      if (!res.data) {
        return;
      }
      if (res.data && res.data.task_calendar_status){
        taskCalendarStatusList.value = res.data.task_calendar_status;
      }
      if (res.data && res.data.activityLogs_types){
        activityLogTypeList.value = res.data.activityLogs_types.filter(item =>
            ['dental_appointment', 'treatment_plan'].includes(item.value)
        )
      }
    })
  ])
})

</script>
